import type { ReactNode } from 'react';
import { Datagrid, SimpleList, type RaRecord } from 'react-admin';
import { Box, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { dataTableScrollSx } from '../ui';

type RecordContent<RecordType extends RaRecord> = (record: RecordType) => ReactNode;

interface ResponsiveDataListProps<RecordType extends RaRecord> {
  children: ReactNode;
  ariaLabel: string;
  primaryText: RecordContent<RecordType>;
  secondaryText?: RecordContent<RecordType>;
  tertiaryText?: RecordContent<RecordType>;
  leftAvatar?: RecordContent<RecordType>;
  leftIcon?: RecordContent<RecordType>;
}

export function ResponsiveDataList<RecordType extends RaRecord>({
  children,
  ariaLabel,
  primaryText,
  secondaryText,
  tertiaryText,
  leftAvatar,
  leftIcon,
}: ResponsiveDataListProps<RecordType>) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });

  if (!compact) {
    return (
      <Box role="region" aria-label={ariaLabel} tabIndex={0} sx={dataTableScrollSx}>
        <Datagrid
          rowClick="edit"
          bulkActionButtons={false}
          sx={{
            width: 'max-content',
            minWidth: '100%',
            '& .RaDatagrid-tableWrapper': { overflow: 'visible' },
            '& .RaDatagrid-table': { width: 'max-content', minWidth: '100%' },
            '& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type': {
              position: 'sticky',
              right: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
              boxShadow: '-10px 0 18px -18px rgba(15,42,61,.65)',
            },
            '& .RaDatagrid-headerCell:last-of-type': { zIndex: 2, bgcolor: '#F7F9FA' },
          }}
        >
          {children}
        </Datagrid>
      </Box>
    );
  }

  return (
    <SimpleList<RecordType>
      aria-label={ariaLabel}
      rowClick="edit"
      primaryText={primaryText}
      secondaryText={secondaryText}
      tertiaryText={tertiaryText}
      leftAvatar={leftAvatar}
      leftIcon={leftIcon}
      rightIcon={() => <ChevronRightRoundedIcon fontSize="small" />}
      rowSx={() => ({
        width: 'calc(100% - 12px)',
        minHeight: 76,
        mx: 0.75,
        my: 0.5,
        px: 1.25,
        py: 1,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        transition: 'background-color .18s ease, border-color .18s ease',
        '&:hover': {
          bgcolor: 'rgba(14,116,144,.04)',
          borderColor: 'rgba(14,116,144,.20)',
        },
        '&:focus-visible': {
          outline: '3px solid rgba(14,116,144,.24)',
          outlineOffset: 1,
        },
      })}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        p: 0.5,
        overflow: 'hidden',
        '& .MuiListItemText-root': { minWidth: 0, my: 0 },
        '& .MuiListItemText-primary': {
          fontWeight: 800,
          lineHeight: 1.35,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
        },
        '& .MuiListItemText-secondary': {
          mt: 0.35,
          lineHeight: 1.4,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
        },
        '& .RaSimpleList-tertiary': {
          display: 'block',
          float: 'none',
          ml: 0,
          mt: 0.25,
          color: 'text.secondary',
          fontSize: 12,
          whiteSpace: 'normal',
          textAlign: 'left',
          overflowWrap: 'anywhere',
        },
        '& .MuiListItemIcon-root': { minWidth: 38, color: 'primary.main' },
      }}
    />
  );
}
